// WaiveFindingModal — Phase 4 G3.7, Tier 1 (2026-05-30).
//
// The in-UI replacement for "go hand-author
// phase4/audit-diff.waivers.json". Shared by all three Phase-4
// findings panels (data-dictionary, threat-model, controls-coverage):
// each "Waive" button opens this modal, the architect records a
// justification, and on submit we POST the waiver and invalidate the
// panel's findings query so the finding comes back flagged `waived`.
//
// Self-contained: it owns its mutation + cache invalidation so the
// panels only need to track which finding is being waived.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, ShieldOff, X } from 'lucide-react'

import { createFindingWaiver } from '@/lib/findingWaiversApi'
import { toast } from '@/hooks/use-toast'

export interface WaiveFindingModalProps {
  projectId: string
  runId: number
  invariantId: string
  findingKey: string
  /** Short human label for the finding being waived (shown in the modal). */
  findingLabel: string
  /** Query key to invalidate on success so the panel refetches. */
  refetchQueryKey: unknown[]
  onClose: () => void
}

export function WaiveFindingModal({
  projectId,
  runId,
  invariantId,
  findingKey,
  findingLabel,
  refetchQueryKey,
  onClose,
}: WaiveFindingModalProps) {
  const queryClient = useQueryClient()
  const [justification, setJustification] = useState('')
  const [acceptedBy, setAcceptedBy] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      createFindingWaiver(projectId, runId, {
        invariant_id: invariantId,
        finding_key: findingKey,
        justification: justification.trim(),
        accepted_by: acceptedBy.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: refetchQueryKey })
      toast({
        variant: 'success',
        title: 'Finding waived',
        description:
          'Recorded in phase4/audit-diff.waivers.json — the pre-export gate will honour it.',
      })
      onClose()
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Could not waive finding',
        description: (err as Error).message,
      }),
  })

  const canSubmit = justification.trim().length >= 8 && !mutation.isPending

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-[560px] overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-900 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
              <ShieldOff className="h-5 w-5 text-amber-400" />
              Waive this finding
            </h2>
            <p className="mt-1 text-sm text-zinc-400">
              Accept the finding as a false positive or an out-of-scope
              risk. The justification ships in the audit package, and the
              pre-export gate will stop blocking on it.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* What's being waived */}
        <div className="mb-4 rounded border border-zinc-800 bg-zinc-950/50 p-3 text-sm">
          <div className="text-xs uppercase tracking-wide text-zinc-500">
            Finding
          </div>
          <div className="mt-1 text-zinc-200">{findingLabel}</div>
          <div className="mt-1 font-mono text-[10.5px] text-zinc-500">
            {invariantId} · {findingKey}
          </div>
        </div>

        {/* Justification */}
        <label className="mb-1 block text-sm font-medium text-zinc-300">
          Justification (required)
        </label>
        <textarea
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          rows={3}
          autoFocus
          className="mb-1 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-amber-600 focus:outline-none"
          placeholder="Why is this finding accepted? e.g. 'Transport layer does not handle authorization — STRIDE-E is N/A for this asset.'"
        />
        <p className="mb-4 text-[11px] text-zinc-500">
          A real sentence, please — this is the audit record an assessor
          reads.
        </p>

        {/* Accepted-by (optional) */}
        <label className="mb-1 block text-sm font-medium text-zinc-300">
          Accepted by <span className="text-zinc-500">(optional)</span>
        </label>
        <input
          type="text"
          value={acceptedBy}
          onChange={(e) => setAcceptedBy(e.target.value)}
          className="mb-4 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-amber-600 focus:outline-none"
          placeholder="Your name or email (defaults to 'G3.7 review surface')"
        />

        {mutation.isError && (
          <div className="mb-3 flex items-start gap-2 rounded border border-red-800 bg-red-950/40 p-2 text-sm text-red-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{(mutation.error as Error).message}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="flex items-center gap-2 rounded bg-amber-600 px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-amber-500"
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Waive finding
          </button>
        </div>
      </div>
    </div>
  )
}
